import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { NavigableEntityType, NavPriority, NavPreference } from "@/lib/navPrefsPure";

/**
 * Private per-user favorite/priority preferences for threads, topics, spaces
 * (table user_entity_preferences, migration 067 — owner-only RLS) and the
 * server-side per-space nav counts (RPC nav_space_counts, migration 068 —
 * SECURITY INVOKER so RLS defines visibility).
 *
 * Mirrors the tagsService pattern: singleton class, session-derived user id,
 * explicit selects, defensive .eq(user_id) alongside RLS. A preference row
 * never grants access to its target (S2); no titles or content ever leave the
 * client through this service (Article 9 minimization — ids only).
 */

export interface SpaceNavCounts {
  spaceId: string;
  topicCount: number;
  threadCount: number;
}

const TABLE = "user_entity_preferences";

class UserEntityPreferencesService {
  private async getCurrentUserId(): Promise<string | null> {
    const session = await getCurrentSession();
    return session?.user?.id ?? null;
  }

  /** All of the current user's nav preferences (small table; single fetch). */
  async getPreferences(): Promise<NavPreference[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await this.getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await supabase
      .from(TABLE)
      .select("entity_type, entity_id, is_favorite, priority")
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: { entity_type: string; entity_id: unknown; is_favorite: unknown; priority: string | null }) => ({
      entityType: r.entity_type as NavigableEntityType,
      entityId: String(r.entity_id),
      isFavorite: Boolean(r.is_favorite),
      priority: (r.priority ?? "none") as NavPriority,
    }));
  }

  /** Upsert one preference (favorite and/or priority). Owner-only via RLS. */
  async setPreference(
    entityType: NavigableEntityType,
    entityId: string,
    patch: { isFavorite?: boolean; priority?: NavPriority },
  ): Promise<void> {
    if (!isSupabaseConfigured) throw new Error("Not configured");
    const userId = await this.getCurrentUserId();
    if (!userId) throw new Error("Not signed in");
    const row: Record<string, unknown> = {
      user_id: userId,
      entity_type: entityType,
      entity_id: entityId,
    };
    if (patch.isFavorite !== undefined) row.is_favorite = patch.isFavorite;
    if (patch.priority !== undefined) row.priority = patch.priority;
    const { error } = await supabase
      .from(TABLE)
      .upsert(row, { onConflict: "user_id,entity_type,entity_id" });
    if (error) throw new Error(error.message);
  }

  /**
   * Remove a preference row entirely (used when both flags return to default —
   * keeps the table tidy and orphan-free).
   */
  async clearPreference(entityType: NavigableEntityType, entityId: string): Promise<void> {
    if (!isSupabaseConfigured) return;
    const userId = await this.getCurrentUserId();
    if (!userId) return;
    const { error } = await supabase
      .from(TABLE)
      .delete()
      .eq("user_id", userId)
      .eq("entity_type", entityType)
      .eq("entity_id", entityId);
    if (error) throw new Error(error.message);
  }

  /** Server-side per-space topic/thread counts (RLS-visible only; no client counting). */
  async getSpaceNavCounts(): Promise<SpaceNavCounts[]> {
    if (!isSupabaseConfigured) return [];
    const { data, error } = await supabase.rpc("nav_space_counts");
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: { space_id: string; topic_count: number; thread_count: number }) => ({
      spaceId: String(r.space_id),
      topicCount: Number(r.topic_count) || 0,
      threadCount: Number(r.thread_count) || 0,
    }));
  }
}

export const userEntityPreferencesService = new UserEntityPreferencesService();

/** Query keys (inline-tuple convention; scoped per user like the sidebar keys). */
export const navPrefsQueryKey = (userId: string | undefined | null) => ["nav-prefs", userId ?? "anon"];
export const navCountsQueryKey = (userId: string | undefined | null) => ["nav-space-counts", userId ?? "anon"];
